import { useCallback, useEffect, useState } from 'react';
import {
  ConnectionMethod, DataSource, DataTypeKey, FieldMapping, IntegrationPoint, MatchKey,
  PointOwner, PreviewResult, SourceSystem, SyncFrequency, SyncRun, isPullMethod,
} from './types';
import { DATA_TYPES } from './catalog';
import { getOffer, getSystemFixture } from './fixtures/sourceCatalog';

// v4: connection methods were re-cut into transports (`apiPull` / `fileFeed` / `webhookPush`) and
// the approval gate moved off that axis, so a v3 blob holds method values that no longer exist.
// Bumping the key is the migration — a demo's saved state is not worth a converter.
export const DEMO_STORAGE_KEY = 'mowazaf.externalIntegrations.demo.v4';

export interface DemoState {
  sources: DataSource[];
  points: IntegrationPoint[];
  syncRuns: SyncRun[];
}

// Empty, not seeded with Mowazaf: this collection holds registered external systems only.
function buildInitialSources(): DataSource[] {
  return [];
}

function buildInitialPoints(): IntegrationPoint[] {
  return DATA_TYPES.map((def) => ({
    dataType: def.key,
    module: def.module,
    owner: 'unset',
    sourceId: null,
    mapping: [],
    enriched: false,
  }));
}

export function buildInitialState(): DemoState {
  return { sources: buildInitialSources(), points: buildInitialPoints(), syncRuns: [] };
}

export function loadDemoState(): DemoState {
  try {
    const raw = window.localStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw) return buildInitialState();
    return JSON.parse(raw) as DemoState;
  } catch {
    return buildInitialState();
  }
}

export function saveDemoState(state: DemoState): void {
  try {
    window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // A full or blocked storage quota must not break the screen.
  }
}

let idCounter = 0;
function newId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${Date.now()}-${idCounter}`;
}

export interface AddSourceInput {
  system: SourceSystem;
  displayName: string;
  method: ConnectionMethod;
  tenantUrl: string;
  clientId: string;
  fileLocation?: string;
  callbackUrl?: string;
  syncFrequency?: SyncFrequency;
  syncTimeOfDay?: string;
  requiresSignedExchange: boolean;
}

export interface IntegrationDemo {
  state: DemoState;
  busyKey: string | null;
  addSource: (input: AddSourceInput, actorName: string) => Promise<string>;
  testSource: (sourceId: string, actorName: string) => Promise<string[]>;
  removeSource: (sourceId: string, actorName: string) => void;
  signDataExchange: (sourceId: string, actorName: string) => void;
  setPointOwner: (dataType: DataTypeKey, owner: PointOwner, sourceId: string | null, actorName: string) => void;
  updateMapping: (dataType: DataTypeKey, mapping: FieldMapping[]) => void;
  setMatchKey: (dataType: DataTypeKey, matchKey: MatchKey) => void;
  buildPreview: (dataType: DataTypeKey) => PreviewResult;
  confirmImport: (dataType: DataTypeKey, actorName: string) => Promise<void>;
  runEnrichment: (dataType: DataTypeKey, actorName: string) => Promise<void>;
  reset: () => void;
}

const SIMULATED_DELAY_MS = 900;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

export function useIntegrationDemo(): IntegrationDemo {
  const [state, setState] = useState<DemoState>(() => loadDemoState());
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => { saveDemoState(state); }, [state]);

  const appendRun = useCallback((run: SyncRun) => {
    setState((prev) => ({ ...prev, syncRuns: [run, ...prev.syncRuns].slice(0, 50) }));
  }, []);

  const addSource = useCallback(async (input: AddSourceInput, actorName: string) => {
    setBusyKey('addSource');
    await delay(SIMULATED_DELAY_MS);
    const now = new Date().toISOString();
    const id = newId('src');
    const fixture = getSystemFixture(input.system);
    setState((prev) => ({
      ...prev,
      sources: [
        ...prev.sources,
        {
          id,
          system: input.system,
          displayName: input.displayName,
          status: 'connected',
          method: input.method,
          tenantUrl: input.tenantUrl,
          clientId: input.clientId,
          grantedScopes: fixture?.scopes ?? [],
          connectedByName: actorName,
          connectedOn: now,
          fileLocation: input.fileLocation,
          callbackUrl: input.callbackUrl,
          syncFrequency: input.syncFrequency,
          syncTimeOfDay: input.syncTimeOfDay,
          requiresSignedExchange: input.requiresSignedExchange,
          // Only a pull has a next run to show. A push is triggered by the source, so there is no
          // schedule to display and claiming one would be a fiction.
          nextScheduledRun: isPullMethod(input.method) ? input.syncTimeOfDay : undefined,
        },
      ],
    }));
    appendRun({
      id: newId('run'), occurredOn: now, sourceId: id, trigger: 'config', actorName,
      created: 0, updated: 0, skipped: 0, durationMs: SIMULATED_DELAY_MS,
      outcome: 'success', messageKey: 'extInt.audit.sourceAdded', messageValue: input.displayName,
    });
    setBusyKey(null);
    return id;
  }, [appendRun]);

  const testSource = useCallback(async (sourceId: string, actorName: string) => {
    setBusyKey(`test:${sourceId}`);
    await delay(SIMULATED_DELAY_MS);
    const source = state.sources.find((s) => s.id === sourceId);
    const scopes = source ? getSystemFixture(source.system)?.scopes ?? [] : [];
    appendRun({
      id: newId('run'), occurredOn: new Date().toISOString(), sourceId, trigger: 'test', actorName,
      created: 0, updated: 0, skipped: 0, durationMs: SIMULATED_DELAY_MS,
      outcome: 'success', messageKey: 'extInt.audit.sourceTested', messageValue: source?.displayName,
    });
    setBusyKey(null);
    return scopes;
  }, [appendRun, state.sources]);

  // Removal is blocked while any point still references the source, so a point can never
  // point at an id that no longer exists.
  const removeSource = useCallback((sourceId: string, actorName: string) => {
    setState((prev) => {
      if (prev.points.some((p) => p.sourceId === sourceId)) return prev;
      const removed = prev.sources.find((s) => s.id === sourceId);
      if (!removed) return prev;
      return { ...prev, sources: prev.sources.filter((s) => s.id !== sourceId) };
    });
    const removed = state.sources.find((s) => s.id === sourceId);
    appendRun({
      id: newId('run'), occurredOn: new Date().toISOString(), sourceId, trigger: 'config', actorName,
      created: 0, updated: 0, skipped: 0, durationMs: 0, outcome: 'success',
      messageKey: 'extInt.audit.sourceRemoved', messageValue: removed?.displayName,
    });
  }, [appendRun, state.sources]);

  const signDataExchange = useCallback((sourceId: string, actorName: string) => {
    const now = new Date().toISOString();
    setState((prev) => ({
      ...prev,
      sources: prev.sources.map((s) =>
        s.id === sourceId ? { ...s, exchangeSignedByName: actorName, exchangeSignedOn: now } : s),
    }));
    appendRun({
      id: newId('run'), occurredOn: now, sourceId, trigger: 'config', actorName,
      created: 0, updated: 0, skipped: 0, durationMs: 0, outcome: 'success',
      messageKey: 'extInt.audit.exchangeSigned',
    });
  }, [appendRun]);

  // Repointing resets the mapping: two systems name the same field differently, so carrying the
  // old system's field names across would silently map the wrong columns.
  const setPointOwner = useCallback((
    dataType: DataTypeKey, owner: PointOwner, sourceId: string | null, actorName: string,
  ) => {
    // The FK is set only for the external answer, which is the pairing Phase 2's CHECK enforces.
    const resolvedSourceId = owner === 'external' ? sourceId : null;
    setState((prev) => {
      const source = resolvedSourceId ? prev.sources.find((s) => s.id === resolvedSourceId) : undefined;
      const offer = source ? getOffer(source.system, dataType) : undefined;
      return {
        ...prev,
        points: prev.points.map((p) => (p.dataType === dataType
          ? {
            ...p,
            owner,
            sourceId: resolvedSourceId,
            mapping: offer ? offer.defaultMapping.map((m) => ({ ...m })) : [],
            matchKey: offer?.defaultMatchKey,
            lastUpdatedOn: undefined,
            enriched: false,
          }
          : p)),
      };
    });
    const messageKey = owner === 'notTracked'
      ? 'extInt.audit.pointNotTracked'
      : owner === 'mowazaf'
        ? 'extInt.audit.pointNative'
        : owner === 'external'
          ? 'extInt.audit.pointSourced'
          : 'extInt.audit.pointCleared';
    appendRun({
      id: newId('run'), occurredOn: new Date().toISOString(), dataType,
      sourceId: resolvedSourceId ?? undefined, trigger: 'config', actorName,
      created: 0, updated: 0, skipped: 0, durationMs: 0, outcome: 'success', messageKey,
    });
  }, [appendRun]);

  const updateMapping = useCallback((dataType: DataTypeKey, mapping: FieldMapping[]) => {
    setState((prev) => ({
      ...prev,
      points: prev.points.map((p) => (p.dataType === dataType ? { ...p, mapping } : p)),
    }));
  }, []);

  const setMatchKey = useCallback((dataType: DataTypeKey, matchKey: MatchKey) => {
    setState((prev) => ({
      ...prev,
      points: prev.points.map((p) => (p.dataType === dataType ? { ...p, matchKey } : p)),
    }));
  }, []);

  const buildPreview = useCallback((dataType: DataTypeKey): PreviewResult => {
    const point = state.points.find((p) => p.dataType === dataType);
    const source = point?.sourceId ? state.sources.find((s) => s.id === point.sourceId) : undefined;
    const offer = source ? getOffer(source.system, dataType) : undefined;
    const warnings = (point?.mapping ?? [])
      .filter((m) => m.valueMap?.some((v) => v.targetValue === null))
      .map((m) => ({
        reasonKey: 'extInt.preview.unmappedValue',
        count: 4,
        exampleLabel: `${m.targetField}: ${m.valueMap?.find((v) => v.targetValue === null)?.sourceValue ?? ''}`,
      }));
    const blockingFields = (point?.mapping ?? [])
      .filter((m) => m.required && !m.sourceField)
      .map((m) => m.targetField);
    return {
      dataType,
      newCount: offer?.newCount ?? 0,
      updatedCount: offer?.updatedCount ?? 0,
      warnings,
      skipped: dataType === 'employees'
        ? [{ reasonKey: 'extInt.preview.noMatchKey', count: 3, exampleLabel: 'EMP-1402' }]
        : [],
      duplicates: dataType === 'employees'
        ? [{ reasonKey: 'extInt.preview.possibleDuplicate', count: 1, exampleLabel: 'L. Haddad' }]
        : [],
      blockingFields,
    };
  }, [state.points, state.sources]);

  const confirmImport = useCallback(async (dataType: DataTypeKey, actorName: string) => {
    setBusyKey(`import:${dataType}`);
    await delay(SIMULATED_DELAY_MS);
    const preview = buildPreview(dataType);
    const now = new Date().toISOString();
    const point = state.points.find((p) => p.dataType === dataType);
    setState((prev) => ({
      ...prev,
      points: prev.points.map((p) => (p.dataType === dataType ? { ...p, lastUpdatedOn: now } : p)),
      sources: prev.sources.map((s) => (s.id === point?.sourceId ? { ...s, lastRunOn: now } : s)),
    }));
    appendRun({
      id: newId('run'), occurredOn: now, dataType, sourceId: point?.sourceId ?? undefined,
      trigger: 'manual', actorName,
      created: preview.newCount, updated: preview.updatedCount,
      skipped: preview.skipped.reduce((sum, s) => sum + s.count, 0),
      durationMs: SIMULATED_DELAY_MS, outcome: 'success',
    });
    setBusyKey(null);
  }, [appendRun, buildPreview, state.points]);

  const runEnrichment = useCallback(async (dataType: DataTypeKey, actorName: string) => {
    setBusyKey(`enrich:${dataType}`);
    await delay(SIMULATED_DELAY_MS * 2);
    setState((prev) => ({
      ...prev,
      points: prev.points.map((p) => (p.dataType === dataType ? { ...p, enriched: true } : p)),
    }));
    appendRun({
      id: newId('run'), occurredOn: new Date().toISOString(), dataType, trigger: 'enrichment',
      actorName, created: 0, updated: 0, skipped: 0,
      durationMs: SIMULATED_DELAY_MS * 2, outcome: 'success',
      messageKey: 'extInt.audit.enriched',
    });
    setBusyKey(null);
  }, [appendRun]);

  const reset = useCallback(() => {
    window.localStorage.removeItem(DEMO_STORAGE_KEY);
    setState(buildInitialState());
  }, []);

  return {
    state, busyKey, addSource, testSource, removeSource, signDataExchange,
    setPointOwner, updateMapping, setMatchKey, buildPreview, confirmImport,
    runEnrichment, reset,
  };
}
